/**
 * Deciding what a file is, and measuring it.
 *
 * One entry point for every supported format, so that uploading, receiving a shared file,
 * and rendering all agree about what a given set of bytes holds. The kind is decided by
 * looking at the content: a name is a hint and nothing more, since files get renamed, and
 * a share carries a name chosen on another machine.
 *
 * Order matters, and it runs from the strongest evidence to the weakest. GLB and 3MF are
 * settled first, on a signature at the very start of the file: binary STL is recognised by
 * an exact length formula, and a formula can be satisfied by coincidence in a way that
 * four fixed bytes cannot. STL comes next, then PLY, which announces itself. glTF, G-code
 * and OBJ are all text and are tested by what they contain — glTF before the other two,
 * being the only one of the three whose test is a structural one rather than a pattern a
 * stray line could match.
 */

import { looksLikeGlb, looksLikeGltfJson } from './gltf.js';
import { looksLikeObj, readObj } from './obj.js';
import { looksLikePly, readPly } from './ply.js';
import { detectStlFormat, TRIANGLE_WARNING_THRESHOLD, validateStl } from './stl.js';
import { looksLike3mf } from './threeMf.js';
import type { FileColourFormat, ModelKind, ModelStats } from './types.js';

export type ModelValidation =
  | { readonly ok: true; readonly stats: ModelStats }
  | { readonly ok: false; readonly message: string };

/** What the upload control offers, and what the error message names. */
export const SUPPORTED_EXTENSIONS = [
  '.stl',
  '.gcode',
  '.gco',
  '.g',
  '.nc',
  '.obj',
  '.ply',
  '.3mf',
  '.gltf',
  '.glb',
] as const;

/** How much of a text file is enough to tell what it is. */
const SNIFF_BYTES = 64 * 1024;

/**
 * Above this many drawn moves, warn that things may get slow.
 *
 * A line segment is cheaper to draw than a triangle, so this sits higher than the triangle
 * threshold; a detailed print is a great many of them all the same.
 */
export const SEGMENT_WARNING_THRESHOLD = 2_000_000;

/**
 * What kind of model a stored record holds.
 *
 * Records written before there was more than one kind carry no `kind` at all, and they are
 * all STL, since that was the only thing that could be stored. Read through here rather
 * than directly, exactly as colours are, so that no migration is needed and an unfamiliar
 * value from a newer version degrades to something drawable rather than to nothing.
 */
export function kindOf(stats: { readonly kind?: ModelKind }): ModelKind {
  const stored = stats.kind;
  return stored !== undefined && KNOWN_KINDS.has(stored) ? stored : 'stl';
}

/**
 * Listed rather than inferred, so an unfamiliar value from a newer version falls back
 * instead of being trusted into a switch that has no case for it.
 */
const KNOWN_KINDS = new Set<ModelKind>(['stl', 'gcode', 'obj', 'ply', '3mf', 'gltf']);

/** Whether this kind is drawn as a surface or as a path. */
export function isToolpath(stats: { readonly kind?: ModelKind }): boolean {
  return kindOf(stats) === 'gcode';
}

/**
 * Whether the file brought its own colours, in which case nothing may override them.
 *
 * Read through here rather than off the field, exactly as `kindOf` is: an absent value
 * means nothing has looked yet, and a record from a newer version could carry a value
 * this one does not know — either way the safe reading is "no", which leaves the model
 * colourable rather than locking a control over a colour that was never found.
 */
export function hasFileColours(stats: {
  readonly fileColours?: FileColourFormat;
}): boolean {
  const format = stats.fileColours;
  return format === 'byte' || format === 'unit' || format === 'material';
}

/**
 * Whether this record has never been examined for colours of its own.
 *
 * True only for a PLY stored before the field existed. Everything else either carries an
 * answer or is of a format that cannot have one, and asking again would be a file read
 * per display for a result that cannot change.
 */
export function colourCheckPending(stats: {
  readonly kind?: ModelKind;
  readonly fileColours?: FileColourFormat;
}): boolean {
  return kindOf(stats) === 'ply' && stats.fileColours === undefined;
}

/**
 * Identify a file's kind without measuring it.
 *
 * Returns undefined for anything unrecognised, which is a routine outcome — people drop
 * the wrong file in — rather than an error.
 */
export function detectModelKind(buffer: ArrayBuffer): ModelKind | undefined {
  if (buffer.byteLength === 0) return undefined;

  // Before the STL test, not after it. Binary STL is recognised by its length matching a
  // formula, and a container of the right size would otherwise be claimed by it — these
  // two are recognised by fixed bytes at the very start, which cannot coincide.
  if (looksLikeGlb(buffer)) return 'gltf';
  if (looksLike3mf(buffer)) return '3mf';

  if (detectStlFormat(buffer) !== 'unknown') return 'stl';
  if (looksLikePly(buffer)) return 'ply';

  const sniff = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(0, SNIFF_BYTES),
  );

  // glTF first: it is JSON, so its test is a shape rather than a pattern, and neither of
  // the other two could produce a document that satisfies it.
  if (looksLikeGltfJson(sniff)) return 'gltf';

  // OBJ before G-code: an OBJ file is unambiguous once a `v` and an `f` line have been
  // seen, whereas G-code's test is a pattern that a stray line could in principle match.
  if (looksLikeObj(sniff)) return 'obj';
  if (looksLikeGcode(sniff)) return 'gcode';

  return undefined;
}

/**
 * Identify and measure a file in one go.
 *
 * Returns a result rather than throwing: a file that cannot be read is an expected outcome
 * the interface reports inline, not an exceptional condition.
 */
export function validateModel(buffer: ArrayBuffer): ModelValidation {
  if (buffer.byteLength === 0) {
    return { ok: false, message: 'The file is empty.' };
  }

  const kind = detectModelKind(buffer);

  switch (kind) {
    case 'stl': {
      const result = validateStl(buffer);
      return result.ok ? result : { ok: false, message: result.message };
    }
    case 'ply': {
      const result = readPly(buffer);
      return result.ok ? result : { ok: false, message: result.message };
    }
    case 'obj': {
      const text = new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      const result = readObj(text);
      return result.ok ? result : { ok: false, message: result.message };
    }
    case 'gcode':
    case '3mf':
    case 'gltf':
      // Measured by the renderer's loaders rather than here — see `readModelFile`. This
      // layer has no three.js in it, which is the point of it, and none of these three can
      // be measured without building something: a toolpath is a program, and the other two
      // are containers holding a scene rather than a list of triangles.
      return {
        ok: false,
        message: `A ${kind === 'gcode' ? 'toolpath' : kind} cannot be measured without building it.`,
      };
    default:
      return {
        ok: false,
        message: `This is not a file this viewer can read. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}.`,
      };
  }
}

/**
 * Does this look like G-code?
 *
 * Asks for a movement command carrying a coordinate, rather than merely for a line
 * beginning with `G`: `G21` and a stray `G` in a comment are both things other text files
 * contain. Comments are stripped first, since a slicer's header is many lines of them.
 *
 * Only sniffing lives here. Reading the file is `GCodeLoader`'s job.
 */
export function looksLikeGcode(text: string): boolean {
  if (text.includes('\0')) return false;

  let checked = 0;
  for (const rawLine of text.split('\n')) {
    const semicolon = rawLine.indexOf(';');
    const line = (semicolon === -1 ? rawLine : rawLine.slice(0, semicolon))
      .replace(/\([^)]*\)/g, ' ')
      .trim();
    if (line === '') continue;

    if (/^G0?[01]\b/i.test(line) && /[XYZE]-?[\d.]/i.test(line)) return true;

    checked += 1;
    // A file that has not reached a move within this many instructions is not one this
    // viewer can do anything with anyway.
    if (checked > 500) return false;
  }

  return false;
}

/**
 * Whether a model is heavy enough to be worth warning about.
 *
 * Triangles and drawn moves are not comparable — a line segment is cheaper than a triangle
 * — so each kind is measured against its own threshold rather than against one number
 * pretending to cover both.
 */
export function isHeavy(stats: ModelStats): boolean {
  return isToolpath(stats)
    ? (stats.segmentCount ?? 0) > SEGMENT_WARNING_THRESHOLD
    : stats.triangleCount > TRIANGLE_WARNING_THRESHOLD;
}
